import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import { LanguageProvider } from "./i18n";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { FloatingCall } from "./components/FloatingCall";
import { LiquidGlassBackground, ScrollProgress } from "./components/LiquidGlass";
import { PageFlipTransition } from "./components/PageFlipTransition";
import { OrbitField } from "./components/OrbitField";
import { UnseenCursor } from "./components/UnseenCursor";
import { FloatingSound } from "./components/FloatingSound";
import { BackgroundMusic } from "./components/BackgroundMusic";
import { assignTempo, notifyTempo, routeTempo } from "./utils/tempo";
import { routeKey } from "./utils/perf";
import { UniverseLayer } from "./components/Universe";
import { AuthCallback } from "./components/AuthCallback";
import Home from "./pages/Home";
import RepairLab from "./pages/RepairLab";
import Showroom from "./pages/Showroom";
import Visit from "./pages/Visit";
import Quote from "./pages/Quote";
import Phones from "./pages/Phones";
import Dashboard from "./pages/Dashboard";

const PageTransition = ({ children }) => (
  <motion.main
    className="overflow-x-clip"
    initial={{ opacity: 0, y: 12, scale: 0.995 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -8, scale: 0.997 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.main>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  // Google sign-in returns with #session_id in the URL fragment — process it
  // synchronously during render BEFORE any route/page logic (playbook rule).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/repair-lab" element={<PageTransition><RepairLab /></PageTransition>} />
        <Route path="/phones" element={<PageTransition><Phones /></PageTransition>} />
        <Route path="/showroom" element={<PageTransition><Showroom /></PageTransition>} />
        <Route path="/visit" element={<PageTransition><Visit /></PageTransition>} />
        <Route path="/quote" element={<PageTransition><Quote /></PageTransition>} />
        <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
        <Route path="*" element={<PageTransition><Home /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Front page animates a little slower; the rest run faster.
const TempoManager = () => {
  const { pathname } = useLocation();
  assignTempo(routeTempo(pathname));
  useEffect(() => {
    notifyTempo();
  }, [pathname]);
  return null;
};

// Per-page accent identity — orange page, blue page, orange, blue...
// One palette, two alternating personalities (owner's call).
const ORANGE = { "--accent-rgb": "199 91 18", "--accent-text-rgb": "240 146 62", "--accent-soft-rgb": "245 200 154" };
const BLUE = { "--accent-rgb": "46 107 230", "--accent-text-rgb": "107 155 255", "--accent-soft-rgb": "169 196 245" };
const PAGE_ACCENTS = {
  "/": ORANGE,
  "/repair-lab": BLUE,
  "/phones": ORANGE,
  "/showroom": BLUE,
  "/visit": ORANGE,
  "/quote": BLUE,
};

const RouteAccent = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const vars = PAGE_ACCENTS[routeKey(pathname)] || ORANGE;
    Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
  }, [pathname]);
  return null;
};

function App() {
  // Native scrolling — the smooth-scroll layer made content feel late.
  return (
    <div className="App relative min-h-screen bg-paper text-ink">
      <UnseenCursor />
      <LanguageProvider>
        <BrowserRouter>
          <LiquidGlassBackground />
          <UniverseLayer />
          <OrbitField />
          <ScrollProgress />
          <ScrollManager />
          <TempoManager />
          <RouteAccent />
          <PageFlipTransition />
          <Header />
          <AnimatedRoutes />
          <Footer />
          <FloatingCall />
          <FloatingSound />
          <BackgroundMusic />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
